import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo, getScrollEventTarget } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth || this.hasAttribute('data-inline-header-search')) {
      window.addEventListener('resize', this.#handleResize);
    }

    if (this.hasAttribute('data-inline-header-search')) {
      const header = document.querySelector('#header-component');
      if (header) {
        this.#headerModeObserver = new MutationObserver(this.#handleResize);
        this.#headerModeObserver.observe(header, { attributes: true, attributeFilter: ['data-menu-style'] });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth || this.hasAttribute('data-inline-header-search')) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.#headerModeObserver?.disconnect();
    this.#stopInlineSearch();
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    if (!this.isConnected) return;
    const { minWidth, maxWidth } = this;

    if (this.hasAttribute('data-inline-header-search') && this.refs.dialog.open) {
      if (this.hasAttribute('data-inline-open') !== this.#usesInlineHeaderSearch) {
        this.closeDialog();
        return;
      }
      this.#positionInlineSearch();
    }

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** @type {AbortController | null} */
  #inlineController = null;
  /** @type {ResizeObserver | null} */
  #inlineHeaderObserver = null;
  /** @type {MutationObserver | null} */
  #headerModeObserver = null;
  /** @type {HTMLElement | null} */
  #previousFocus = null;
  /** @type {number | null} */
  #inlineFrame = null;
  #inlineClosing = false;

  get #usesInlineHeaderSearch() {
    const header = document.querySelector('#header-component');
    return (
      this.hasAttribute('data-inline-header-search') &&
      header instanceof HTMLElement
    );
  }

  /** @param {boolean} open */
  #setSearchTriggerState(open) {
    if (!this.hasAttribute('data-inline-header-search')) return;
    for (const trigger of document.querySelectorAll('[aria-controls="search-modal"]')) {
      trigger.setAttribute('aria-expanded', String(open));
    }
  }

  #positionInlineSearch = () => {
    if (!this.hasAttribute('data-inline-open')) return;
    const header = document.querySelector('#header-component');
    if (!header) return;
    const top = Math.max(0, header.getBoundingClientRect().bottom);
    const viewport = window.visualViewport;
    this.style.setProperty('--cl-search-top', `${top}px`);
    this.style.setProperty('--cl-search-available', `${Math.max(56, (viewport ? viewport.height + viewport.offsetTop : window.innerHeight) - top)}px`);
  };

  #scheduleInlinePosition = () => {
    if (this.#inlineFrame !== null) return;
    this.#inlineFrame = requestAnimationFrame(() => {
      this.#inlineFrame = null;
      this.#positionInlineSearch();
    });
  };

  #startInlineSearch() {
    this.setAttribute('data-inline-open', '');
    this.#positionInlineSearch();
    this.#inlineController = new AbortController();
    const { signal } = this.#inlineController;
    getScrollEventTarget().addEventListener('scroll', this.#scheduleInlinePosition, { passive: true, signal });
    document.addEventListener('pointerdown', this.#handleInlineOutside, { signal });
    window.visualViewport?.addEventListener('resize', this.#scheduleInlinePosition, { signal });
    window.visualViewport?.addEventListener('scroll', this.#scheduleInlinePosition, { passive: true, signal });
    const header = document.querySelector('#header-component');
    if (header) {
      this.#inlineHeaderObserver = new ResizeObserver(this.#positionInlineSearch);
      this.#inlineHeaderObserver.observe(header);
    }
  }

  #stopInlineSearch() {
    this.#inlineController?.abort();
    this.#inlineController = null;
    this.#inlineHeaderObserver?.disconnect();
    this.#inlineHeaderObserver = null;
    if (this.#inlineFrame !== null) cancelAnimationFrame(this.#inlineFrame);
    this.#inlineFrame = null;
    this.removeAttribute('data-inline-open');
    this.#setSearchTriggerState(false);
  }

  /** @param {PointerEvent} event */
  #handleInlineOutside = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('[aria-controls="search-modal"]')) return;
    if (!this.refs.dialog.contains(event.target)) this.closeDialog();
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#previousScrollY = getScrollTop();
    this.#previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (dialog.open || !this.isConnected) return;

      if (this.#usesInlineHeaderSearch) {
        this.#startInlineSearch();
        dialog.show();
      } else {
        lockScroll(dialog);
        dialog.showModal();
      }
      this.#setSearchTriggerState(true);
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    if (this.hasAttribute('data-inline-open')) {
      if (this.#inlineClosing) return;
      this.#inlineClosing = true;
      dialog.classList.add('dialog-closing');
      await onAnimationEnd(dialog, undefined, { subtree: false });
      const restoreFocus = dialog.contains(document.activeElement);
      dialog.close();
      dialog.classList.remove('dialog-closing');
      this.#inlineClosing = false;
      this.#stopInlineSearch();
      if (restoreFocus && this.#previousFocus?.isConnected) this.#previousFocus.focus({ preventScroll: true });
      this.dispatchEvent(new DialogCloseEvent());
      return;
    }

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.#setSearchTriggerState(false);

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);

